/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {firstValueFrom} from 'rxjs';

import {ApiConfig} from '../config/api-config';
import {AdnHttpRequest, AdnHttpService} from '../../components/util/adn-http.service';

@Injectable({
  providedIn: 'root'
})
export class ApiAuthService {
  private authResource = '/authenticate';
  private authUserResource = '/user/reset-password';
  private httpConf: AdnHttpRequest = {
    method: 'POST',
    url: '',
    params: {
      context: null
    },
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    // Auth endpoints must not enter the 401 buffer / refresh loop.
    ignoreAuthModule: true
  };

  constructor(
    private readonly AdnHttp: AdnHttpService,
    private readonly apiConfig: ApiConfig
  ) {
  }

  setResource(val: string): this {
    this.authResource = val;
    return this;
  }

  updatePassword(password: any, authToken: any, twoFactorAuthCode?: any): any {
    const url = this.apiConfig.getVersionedUri(this.authUserResource) + '?auth_token=' + encodeURIComponent(authToken);
    return this.post(url, 'password=' + encodeURIComponent(password) + (twoFactorAuthCode ? '&twoFactorAuthCode=' + encodeURIComponent(twoFactorAuthCode) : '')).then(function(res: any) {
      return res.data;
    });
  }

  resetPassword(username: any): any {
    return this.post(this.apiConfig.getUri(this.authResource + '/reset'), 'username=' + encodeURIComponent(username)).then(function(res: any) {
      return res.data;
    });
  }

  authenticate(username: any, password: any, masqUsername?: any): any {
    const params = 'grant_type=password&scope=ng_api&username=' + encodeURIComponent(username) + '&password=' + encodeURIComponent(password) + (masqUsername ? '&masqueradeAsUser=' + encodeURIComponent(masqUsername) : '');
    return this.post(this.apiConfig.getUri(this.authResource), params).then(function(res: any) {
      return res.data;
    }, function(error: any) {
      // The Authentication resource only returns 401 for 2fa, for invalid credentials its 400
      if (error.status === 401) {
        return error.data;
      }
      return Promise.reject(error);
    });
  }

  twoFactorAuth(twoFactorAuthCode: any, authToken: any): any {
    return this.post(this.apiConfig.getUri(this.authResource + '/2fa'),
      'twoFactorAuthCode=' + encodeURIComponent(twoFactorAuthCode) + '&auth_token=' + encodeURIComponent(authToken),
      {auth_token: authToken}).then(function(res: any) {
      return res.data;
    });
  }

  refresh(refreshToken: any): any {
    return this.post(this.apiConfig.getUri(this.authResource),
      'grant_type=refresh_token&refresh_token=' + encodeURIComponent(refreshToken)).then(function(res: any) {
      return res.data;
    });
  }

  private post(url: string, body: string, params?: Record<string, any>): Promise<any> {
    return firstValueFrom(this.AdnHttp.request$(_.assign({}, this.httpConf, {
      url,
      body,
      params: _.assign({}, this.httpConf.params, params)
    })));
  }
}
